import { useState } from 'react'

import { useEffectOnce } from 'react-use'

import { builderLightText } from '@/blocks/utils'

import type { BackgroundColor } from '@/components/Container'
import { Icon } from '@/components/Icon'
import { Typography, type TypographyColorModifier } from '@/components/Typography'

import { usePersistentState } from '@/hooks/usePersistentState'

import { COOKIES } from './constants'
import { SimpleAnnouncementBarCloseButton, SimpleAnnouncementBarContainer } from './styles'

export type VerticalPositionProps = 'bottom' | 'top'
export type VerticalSpacingProps = 'sm' | 'lg'

export interface SimpleAnnouncementBarProps {
  message: string
  allowPersistentState?: boolean
  background?: BackgroundColor
  backgroundColor?: string
  colorModifier?: TypographyColorModifier
  verticalPosition?: VerticalPositionProps
  bottomSpacing?: VerticalSpacingProps
}

const SimpleAnnouncementBar = ({
  message,
  allowPersistentState,
  backgroundColor,
  background = 'primary',
  colorModifier = 'light',
  verticalPosition = 'bottom',
  bottomSpacing = 'sm',
}: SimpleAnnouncementBarProps) => {
  const persistent = usePersistentState({ ...COOKIES })
  const [hasBeenClosed, setHasBeenClosed] = useState(true)
  const lightText = builderLightText(colorModifier)

  const handleSimpleAnnouncementBarClose = () => {
    setHasBeenClosed(true)
    persistent.createPersistentState()
  }

  useEffectOnce(() => {
    const shouldCloseAnnouncementBar = persistent.state

    if (allowPersistentState && shouldCloseAnnouncementBar) return
    if (!allowPersistentState || !shouldCloseAnnouncementBar) setHasBeenClosed(false)
  })

  if (hasBeenClosed) return null

  return (
    <SimpleAnnouncementBarContainer
      contentClassName="announcement-bar-content"
      backgroundColor={background}
      backgroundColorHex={backgroundColor}
      verticalPosition={verticalPosition}
      verticalSpacingSize={bottomSpacing}
    >
      <Typography tag="div" dark="medium" html={message} light={lightText} />
      <SimpleAnnouncementBarCloseButton
        onClick={handleSimpleAnnouncementBarClose}
        fontColorMode={colorModifier}
        aria-label="Fechar aviso"
        modifier="solid-invert"
      >
        <Icon icon="close" width={20} height={20} />
      </SimpleAnnouncementBarCloseButton>
    </SimpleAnnouncementBarContainer>
  )
}

export default SimpleAnnouncementBar
